/**
 * The Robotics Proposal inbox: one row per submission of the "Request a Robotics
 * Proposal" form, with the handful of answers worth scanning. The full submission,
 * notes and the convert-to-lead action live on the detail page.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import {
  fetchRoboticsProposals,
  proposalCompanyLabel,
  proposalContactName,
  proposalIndustryLabel,
  proposalSearchText,
  roboticsProposalPath,
  ROBOTICS_PROPOSAL_STATUSES,
  type RoboticsProposalRow,
  type RoboticsProposalStatus,
} from '../../api/roboticsProposals';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The solutions checkbox group can hold a dozen options, which would swamp the row.
 * Show the first two and count the rest; the detail page lists them all.
 */
function solutionsSummary(p: RoboticsProposalRow): string {
  const solutions = Array.isArray(p.solutions_of_interest) ? p.solutions_of_interest : [];
  if (solutions.length === 0) return '—';
  if (solutions.length <= 2) return solutions.join(', ');
  return `${solutions.slice(0, 2).join(', ')} +${solutions.length - 2}`;
}

interface Props {
  userId: number;
}

const RoboticsProposalsSection: React.FC<Props> = ({ userId }) => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<RoboticsProposalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | RoboticsProposalStatus>('Pending');

  const { sortKey, sortDir, toggleSort } = useTableSortState();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setProposals(await fetchRoboticsProposals(userId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load proposal requests.');
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingCount = useMemo(
    () => proposals.filter((p) => p.status === 'Pending').length,
    [proposals]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return proposals.filter((p) => {
      if (statusFilter !== 'All' && p.status !== statusFilter) return false;
      // Searches every answer, not just the columns shown, so a term from deep in
      // the form still finds the submission.
      return !term || proposalSearchText(p).includes(term);
    });
  }, [proposals, search, statusFilter]);

  const sorted = useMemo(
    () =>
      sortRows(visible, sortKey, sortDir, (p, key) => {
        switch (key) {
          case 'company':
            return proposalCompanyLabel(p);
          case 'contact':
            return proposalContactName(p);
          case 'industry':
            return proposalIndustryLabel(p);
          case 'timeline':
            return p.expected_timeline;
          case 'solutions':
            return solutionsSummary(p);
          case 'status':
            return p.status;
          case 'received':
            return p.created_at ?? '';
          default:
            return '';
        }
      }),
    [visible, sortKey, sortDir]
  );

  return (
    <section className="events-table-section" aria-labelledby="robotics-proposals-heading">
      <h2 className="events-section-title" id="robotics-proposals-heading">
        Robotics Proposal Requests{' '}
        <span className="events-section-count">({proposals.length})</span>
        {pendingCount > 0 ? (
          <span className="website-lead-new-badge">{pendingCount} pending</span>
        ) : null}
      </h2>
      <p className="page-subtitle website-lead-intro">
        Submissions from the Request a Robotics Proposal form on the website. Open one to see every
        answer, add notes, and add it to the client database as a lead.
      </p>

      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search company, contact, industry, tasks, any answer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search robotics proposal requests"
        />
        <div className="website-lead-filters" role="group" aria-label="Filter by status">
          {(['All', ...ROBOTICS_PROPOSAL_STATUSES] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`website-lead-filter${statusFilter === s ? ' website-lead-filter--active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading proposal requests…</p>
      ) : (
        <div className="events-table-wrap">
          <table className="tasks-search-results-table website-lead-table">
            <thead>
              <tr>
                <SortableTh label="Company" columnKey="company" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Contact" columnKey="contact" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Industry" columnKey="industry" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Interested in" columnKey="solutions" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Timeline" columnKey="timeline" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Received" columnKey="received" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="events-empty-cell">
                    {proposals.length === 0
                      ? 'No proposal requests yet. They appear here as soon as the website posts them.'
                      : 'No proposal requests match this filter.'}
                  </td>
                </tr>
              ) : (
                sorted.map((p) => (
                  <tr
                    key={p.id}
                    className="events-table-row-clickable"
                    onClick={() => navigate(roboticsProposalPath(p.id))}
                  >
                    <td data-label="Company">
                      {/* A real link as well as the row handler, so the row can be
                          opened in a new tab. */}
                      <Link
                        to={roboticsProposalPath(p.id)}
                        className="website-lead-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {proposalCompanyLabel(p)}
                      </Link>
                    </td>
                    <td data-label="Contact">
                      {proposalContactName(p)}
                      {p.job_title ? (
                        <span className="website-lead-muted"> — {p.job_title}</span>
                      ) : null}
                    </td>
                    <td data-label="Industry">{proposalIndustryLabel(p) || '—'}</td>
                    <td data-label="Interested in">{solutionsSummary(p)}</td>
                    <td data-label="Timeline">{p.expected_timeline || '—'}</td>
                    <td data-label="Status">
                      <span
                        className={`robotics-proposal-status robotics-proposal-status--${p.status.toLowerCase()}`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td data-label="Received">{formatDateTime(p.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default RoboticsProposalsSection;
